<template>

  <v-input v-bind="{ ...$attrs, ...commonAttrs }" class="v-datetime-field">

    <v-row>

      <v-col cols="12" md="6">
        <o-text-field
          :label="dateLabel"
          v-model="formState.date"
          type="date"          
          hide-details
        />
      </v-col>

      <v-col cols="12" md="6">
        <o-autocomplete
          :label="timeLabel"
          v-model="formState.time"
          :items="timeOptions"          
          hide-details
        />
      </v-col>

    </v-row>

    <!-- https://stackoverflow.com/a/71678136/19018560 -->
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" />
    </template>

  </v-input>

</template>


<script setup>

import { computed, onMounted, reactive, watch } from 'vue'
import OAutocomplete from './OAutocomplete.vue'
import { Temporal } from '@js-temporal/polyfill'

const props = defineProps({

  validator: {
    type: Object,
  },

  dateLabel: {
    type: String,
  },

  timeLabel: {
    type: String,
  },

  step: {
    // in minutes
    type: Number,
    default: 1,
    validator(value) {
      return [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30].includes(value)
    },
  },

})

const model = defineModel() // string in ISO 8601 format; empty value is ambiguous

const formState = reactive({
  date: '',
  time: '',
})

onMounted(() => {
  if (model.value) {
    const datetime = Temporal.PlainDateTime.from(model.value)
    formState.date = datetime.toPlainDate().toString()
    formState.time = datetime.toPlainTime().toString()
  }
})

const timeOptions = computed(() => {

  const options = []
  let minutesAfterMidnight = 0
  let hour, minute
  let hour12Str, hour24Str, minuteStr, meridiem

  while (minutesAfterMidnight < 1440) {
    hour = Math.floor(minutesAfterMidnight / 60)
    minute = minutesAfterMidnight % 60
    hour12Str = ((hour % 12) || 12).toString().padStart(2, '0')
    meridiem = hour < 12 ? 'am' : 'pm'
    hour24Str = hour.toString().padStart(2, '0')
    minuteStr = minute.toString().padStart(2, '0')
    options.push({
      value: `${hour24Str}:${minuteStr}:00`,
      title: `${hour12Str}:${minuteStr} ${meridiem}`,
    })
    minutesAfterMidnight += props.step
  }

  return options

})

const commonAttrs = computed(() => {

  const output = {}

  if (props.validator) {
    // output.validator = null // don't pass the validator prop along to v-input
    output.errorMessages = props.validator.$errors.map(e => e.$message)
  }

  return output

})

watch(formState, () => {
  let modelValue = null
  if (formState.date && formState.time) {
    modelValue = `${formState.date}T${formState.time}`
  }
  if (modelValue !== model.value) { // avoid unnecessarily triggering watchers, etc.
    model.value = modelValue
  }
})

</script>

<style lang="scss">

// imitate .v-text-field
.v-datetime-field {
  .v-input__details {
    padding-inline: 16px;
  }
}

</style>
